// File: frontend/src/components/admin/AdminTopicOps.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pagination } from "../../utils/Pagination";
import { FaExchangeAlt, FaObjectGroup, FaSearch, FaExclamationTriangle } from "react-icons/fa";
import {
  TopicOpsType,
  TopicOpsTarget,
  TopicOpsEntry,
  TopicMergeHistoryItem,
  searchTopics,
  listTopicEntries,
  getMergeHistory,
  mergeTopics,
  moveEntries,
} from "../../services/adminTopicOpsService";
import { ADMIN_TOPIC_TITLE_MAX_LENGTH } from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { AdminCharCounter } from "./common";
import { InputClearButton } from "../common/InputClearButton";
import "./AdminTopicOps.css";

// No props: both tabs of this screen are open to ADMIN and MODERATOR, so there is
// nothing left for the parent to gate.

// All five topic domains handled in parallel — project-wide invariant.
// Button order requested by admin UX: Gündem, Maç, Kulüp, Kişi, Takım.
const TOPIC_TYPES: TopicOpsType[] = ["CUSTOM_TOPIC", "MATCH", "CLUB", "PERSON", "TEAM"];

const TYPE_LABEL_KEY: Record<TopicOpsType, string> = {
  MATCH: "admin_topicops_type_match",
  CLUB: "admin_topicops_type_club",
  TEAM: "admin_topicops_type_team",
  PERSON: "admin_topicops_type_person",
  CUSTOM_TOPIC: "admin_topicops_type_custom",
};

const PAGE_SIZE = 20;

/**
 * Reusable debounced topic autocomplete.
 * Disables rows that cannot be picked (merged topics always; inactive topics
 * when allowInactive is false; nickaltı topics when allowNickalti is false).
 */
const TopicSearchBox: React.FC<{
  type: TopicOpsType;
  selected: TopicOpsTarget | null;
  onSelect: (t: TopicOpsTarget | null) => void;
  placeholder: string;
  allowInactive?: boolean;
  allowNickalti?: boolean;
}> = ({ type, selected, onSelect, placeholder, allowInactive = false, allowNickalti = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TopicOpsTarget[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  // Debounce timer + stale-response guard (useRef to avoid stale closures)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  // Reset the box when the topic type changes
  useEffect(() => {
    setQuery("");
    setResults([]);
    setOpen(false);
    onSelect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleChange = (value: string) => {
    setQuery(value);
    onSelect(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (value.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      setSearching(true);
      try {
        const res = await searchTopics(type, value.trim());
        if (seq === seqRef.current) {
          setResults(res);
          setOpen(true);
        }
      } catch {
        if (seq === seqRef.current) setResults([]);
      } finally {
        if (seq === seqRef.current) setSearching(false);
      }
    }, 350);
  };

  const isDisabled = (r: TopicOpsTarget): boolean => {
    if (r.merged) return true;
    if (!allowInactive && !r.active) return true;
    if (!allowNickalti && r.nickalti) return true;
    return false;
  };

  return (
    <div className="topicops-searchbox">
      {selected ? (
        <div className="topicops-selected">
          <span className="topicops-selected-title">{selected.title}</span>
          <span className="topicops-selected-count">
            {t("admin_topicops_entry_count", { count: selected.entryCount })}
          </span>
          {selected.nickalti && (
            <span className="topicops-badge topicops-badge-nickalti">
              {t("admin_topicops_nickalti_badge")}
            </span>
          )}
          <button
            type="button"
            className="topicops-clear-btn"
            onClick={() => { onSelect(null); setQuery(""); }}
          >
            ×
          </button>
        </div>
      ) : (
        <>
          <div className="topicops-input-wrap">
            <FaSearch className="topicops-input-icon" />
            <input
              type="text"
              className="topicops-input"
              value={query}
              placeholder={placeholder}
              onChange={(e) => handleChange(e.target.value)}
              onFocus={() => results.length > 0 && setOpen(true)}
            />
            {/* Clear-X — also drops the result list so the dropdown does not
                reopen on the next focus with results for a query that is gone */}
            {query !== "" && (
              <InputClearButton
                onClear={() => {
                  setQuery("");
                  setResults([]);
                  setOpen(false);
                  onSelect(null);
                }}
              />
            )}
          </div>
          {open && (
            <ul className="topicops-results">
              {searching && (
                <li className="topicops-result-empty">{t("loading", "Yükleniyor...")}</li>
              )}
              {!searching && results.length === 0 && (
                <li className="topicops-result-empty">{t("admin_topicops_no_results")}</li>
              )}
              {!searching && results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className="topicops-result"
                    disabled={isDisabled(r)}
                    onClick={() => { onSelect(r); setOpen(false); }}
                  >
                    <span className="topicops-result-title">{r.title}</span>
                    <span className="topicops-result-meta">
                      {t("admin_topicops_entry_count", { count: r.entryCount })}
                      {!r.active && (
                        <span className="topicops-badge topicops-badge-inactive">
                          {t("admin_topicops_inactive_badge")}
                        </span>
                      )}
                      {r.merged && (
                        <span className="topicops-badge topicops-badge-merged">
                          {t("admin_topicops_merged_badge")}
                        </span>
                      )}
                      {r.nickalti && (
                        <span className="topicops-badge topicops-badge-nickalti">
                          {t("admin_topicops_nickalti_badge")}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export const AdminTopicOps: React.FC = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"merge" | "move">("merge");

  // ==================== MERGE state ====================
  const [mergeType, setMergeType] = useState<TopicOpsType>("CUSTOM_TOPIC");
  const [mergeSource, setMergeSource] = useState<TopicOpsTarget | null>(null);
  const [mergeTarget, setMergeTarget] = useState<TopicOpsTarget | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [merging, setMerging] = useState(false);
  const [history, setHistory] = useState<TopicMergeHistoryItem[]>([]);

  // ==================== MOVE state ====================
  const [moveSourceType, setMoveSourceType] = useState<TopicOpsType>("CUSTOM_TOPIC");
  const [moveSource, setMoveSource] = useState<TopicOpsTarget | null>(null);
  const [entries, setEntries] = useState<TopicOpsEntry[]>([]);
  const [entryPage, setEntryPage] = useState(0);
  const [entryTotalPages, setEntryTotalPages] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [moveTargetType, setMoveTargetType] = useState<TopicOpsType>("CUSTOM_TOPIC");
  const [moveTarget, setMoveTarget] = useState<TopicOpsTarget | null>(null);
  const [moving, setMoving] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);

  // ==================== shared feedback ====================
  const [message, setMessage] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await getMergeHistory());
    } catch {
      // History is auxiliary — swallow errors silently
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const loadEntries = useCallback(async (source: TopicOpsTarget, page: number) => {
    setLoadingEntries(true);
    try {
      const res = await listTopicEntries(source.type, source.id, page, PAGE_SIZE);
      setEntries(res.content);
      setEntryPage(res.currentPage);
      setEntryTotalPages(res.totalPages);
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message || t("admin_topicops_error") });
    } finally {
      setLoadingEntries(false);
    }
  }, [t]);

  // Load the entry picker whenever the move source changes
  useEffect(() => {
    setSelectedIds(new Set());
    setEntries([]);
    setEntryPage(0);
    setEntryTotalPages(0);
    if (moveSource) loadEntries(moveSource, 0);
  }, [moveSource, loadEntries]);

  // ==================== MERGE actions ====================

  // Merge is open to ADMIN and MODERATOR alike (AdminTopicOpsService.mergeTopics).
  // What still gates the button is the typed confirmation, because the operation
  // cannot be undone.
  const mergeReady =
    mergeSource !== null &&
    mergeTarget !== null &&
    mergeSource.id !== mergeTarget.id &&
    confirmText.trim() === (mergeTarget?.title ?? "").trim();

  const handleMerge = async () => {
    if (!mergeSource || !mergeTarget || merging) return;
    if (mergeSource.id === mergeTarget.id) {
      setMessage({ kind: "error", text: t("admin_topicops_same_topic_error") });
      return;
    }
    setMerging(true);
    setMessage(null);
    try {
      const res = await mergeTopics(mergeType, mergeSource.id, mergeTarget.id);
      setMessage({
        kind: "success",
        text: t("admin_topicops_merge_success", {
          count: res.movedEntryCount,
          target: res.targetTitle,
        }),
      });
      setMergeSource(null);
      setMergeTarget(null);
      setConfirmText("");
      loadHistory();
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message || t("admin_topicops_error") });
    } finally {
      setMerging(false);
    }
  };

  // ==================== MOVE actions ====================

  const toggleEntry = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = entries.every((e) => next.has(e.id));
      entries.forEach((e) => (allSelected ? next.delete(e.id) : next.add(e.id)));
      return next;
    });
  };

  const moveReady =
    moveSource !== null &&
    moveTarget !== null &&
    selectedIds.size > 0 &&
    !(moveSource.type === moveTarget.type && moveSource.id === moveTarget.id);

  const handleMove = async () => {
    if (!moveTarget || selectedIds.size === 0 || moving) return;
    setMoving(true);
    setMessage(null);
    try {
      const res = await moveEntries(Array.from(selectedIds), moveTargetType, moveTarget.id);
      setMessage({
        kind: "success",
        text: t("admin_topicops_move_success", {
          count: res.movedCount,
          target: moveTarget.title,
        }),
      });
      setSelectedIds(new Set());
      if (moveSource) loadEntries(moveSource, entryPage);
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message || t("admin_topicops_error") });
    } finally {
      setMoving(false);
    }
  };

  // ==================== render helpers ====================

  const renderTypeSelector = (value: TopicOpsType, onChange: (tp: TopicOpsType) => void) => (
    <div className="topicops-type-selector">
      {TOPIC_TYPES.map((tp) => (
        <button
          key={tp}
          type="button"
          className={`topicops-type-btn ${value === tp ? "active" : ""}`}
          onClick={() => onChange(tp)}
        >
          {t(TYPE_LABEL_KEY[tp])}
        </button>
      ))}
    </div>
  );

  const formatDate = (iso: string) => new Date(iso).toLocaleString();

  return (
    <div className="topicops-container">
      {/* Mode switch: merge vs move */}
      <div className="topicops-mode-tabs">
        <button
          type="button"
          className={`topicops-mode-tab ${mode === "merge" ? "active" : ""}`}
          onClick={() => { setMode("merge"); setMessage(null); }}
        >
          <FaObjectGroup /> {t("admin_topicops_merge_tab")}
        </button>
        <button
          type="button"
          className={`topicops-mode-tab ${mode === "move" ? "active" : ""}`}
          onClick={() => { setMode("move"); setMessage(null); }}
        >
          <FaExchangeAlt /> {t("admin_topicops_move_tab")}
        </button>
      </div>

      {message && (
        <div className={`topicops-message topicops-message-${message.kind}`}>
          {message.text}
        </div>
      )}

      {/* ==================== MERGE ==================== */}
      {mode === "merge" && (
        <div className="topicops-panel">
          <label className="topicops-label">{t("admin_topicops_type_label")}</label>
          {renderTypeSelector(mergeType, (tp) => {
            setMergeType(tp);
            setConfirmText("");
          })}

          <div className="topicops-merge-grid">
            <div>
              <label className="topicops-label">{t("admin_topicops_source_label")}</label>
              <TopicSearchBox
                type={mergeType}
                selected={mergeSource}
                onSelect={setMergeSource}
                placeholder={t("admin_topicops_search_placeholder")}
                allowInactive={true}
              />
            </div>
            <div className="topicops-merge-arrow">→</div>
            <div>
              <label className="topicops-label">{t("admin_topicops_target_label")}</label>
              <TopicSearchBox
                type={mergeType}
                selected={mergeTarget}
                onSelect={(v) => { setMergeTarget(v); setConfirmText(""); }}
                placeholder={t("admin_topicops_search_placeholder")}
              />
            </div>
          </div>

          {mergeSource && mergeTarget && mergeSource.id === mergeTarget.id && (
            <div className="topicops-warning">
              <FaExclamationTriangle /> {t("admin_topicops_same_topic_error")}
            </div>
          )}

          {mergeSource && mergeTarget && mergeSource.id !== mergeTarget.id && (
            <div className="topicops-merge-summary">
              <p>
                {t("admin_topicops_merge_summary", {
                  count: mergeSource.entryCount,
                  source: mergeSource.title,
                  target: mergeTarget.title,
                })}
              </p>
              <p className="topicops-irreversible">
                <FaExclamationTriangle /> {t("admin_topicops_merge_warning")}
              </p>
              <label className="topicops-label">
                {t("admin_topicops_confirm_label", { target: mergeTarget.title })}
              </label>
              <input
                type="text"
                className="topicops-input topicops-confirm-input"
                value={confirmText}
                maxLength={ADMIN_TOPIC_TITLE_MAX_LENGTH}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={mergeTarget.title}
              />
              <AdminCharCounter value={confirmText} max={ADMIN_TOPIC_TITLE_MAX_LENGTH} />
              <button
                type="button"
                className="topicops-danger-btn"
                disabled={!mergeReady || merging}
                onClick={handleMerge}
              >
                {merging ? t("loading", "Yükleniyor...") : t("admin_topicops_merge_button")}
              </button>
            </div>
          )}

          {/* Merge history */}
          <h3 className="topicops-history-title">{t("admin_topicops_history_title")}</h3>
          {history.length === 0 ? (
            <p className="topicops-history-empty">{t("admin_topicops_history_empty")}</p>
          ) : (
            <div className="topicops-table-wrap">
              <table className="topicops-table">
                <thead>
                  <tr>
                    <th>{t("admin_topicops_type_label")}</th>
                    <th>{t("admin_topicops_history_source")}</th>
                    <th>{t("admin_topicops_history_target")}</th>
                    <th>{t("admin_topicops_history_count")}</th>
                    <th>{t("admin_topicops_history_by")}</th>
                    <th>{t("admin_topicops_history_date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td>{t(TYPE_LABEL_KEY[h.topicType])}</td>
                      <td>{h.sourceTitle ?? h.sourceSlug}</td>
                      <td>{h.targetTitle ?? h.targetSlug}</td>
                      <td>{h.movedEntryCount}</td>
                      <td>{h.mergedByUsername ?? h.mergedBy}</td>
                      <td>{formatDate(h.mergedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ==================== MOVE ==================== */}
      {mode === "move" && (
        <div className="topicops-panel">
          <label className="topicops-label">{t("admin_topicops_move_source_hint")}</label>
          {renderTypeSelector(moveSourceType, setMoveSourceType)}
          <TopicSearchBox
            type={moveSourceType}
            selected={moveSource}
            onSelect={setMoveSource}
            placeholder={t("admin_topicops_search_placeholder")}
            allowInactive={true}
            allowNickalti={true}
          />

          {moveSource && (
            <>
              <h3 className="topicops-history-title">
                {t("admin_topicops_entries_title", { title: moveSource.title })}
              </h3>
              {loadingEntries ? (
                <p className="topicops-history-empty">{t("loading", "Yükleniyor...")}</p>
              ) : entries.length === 0 ? (
                <p className="topicops-history-empty">{t("admin_topicops_no_entries")}</p>
              ) : (
                <>
                  <div className="topicops-entry-toolbar">
                    <button type="button" className="topicops-link-btn" onClick={toggleAllOnPage}>
                      {t("admin_topicops_select_all")}
                    </button>
                    <span className="topicops-selected-info">
                      {t("admin_topicops_selected_count", { count: selectedIds.size })}
                    </span>
                  </div>
                  <ul className="topicops-entry-list">
                    {entries.map((e) => (
                      <li key={e.id} className="topicops-entry-row">
                        <label className="topicops-entry-label">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(e.id)}
                            onChange={() => toggleEntry(e.id)}
                          />
                          <span className="topicops-entry-snippet">{e.snippet}</span>
                          <span className="topicops-entry-meta">
                            #{e.id} · {e.authorUsername} · {formatDate(e.createdAt)}
                            {e.deleted && (
                              <span className="topicops-badge topicops-badge-inactive">
                                {t("admin_topicops_deleted_badge")}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  {entryTotalPages > 1 && (
                    <div className="topicops-pagination">
                      {/* Shared Pagination component — 1-indexed, while the
                          loader keeps 0-indexed pages. totalItems only needs to
                          be non-zero here; the exact count is not displayed. */}
                      <Pagination
                        currentPage={entryPage + 1}
                        totalPages={entryTotalPages}
                        totalItems={entries.length}
                        paginate={(page) => moveSource && loadEntries(moveSource, page - 1)}
                      />
                    </div>
                  )}
                </>
              )}

              <label className="topicops-label">{t("admin_topicops_move_target_label")}</label>
              {renderTypeSelector(moveTargetType, (tp) => {
                setMoveTargetType(tp);
                setMoveTarget(null);
              })}
              <TopicSearchBox
                type={moveTargetType}
                selected={moveTarget}
                onSelect={setMoveTarget}
                placeholder={t("admin_topicops_search_placeholder")}
                allowNickalti={true}
              />

              {moveTarget?.nickalti && (
                <div className="topicops-warning">
                  <FaExclamationTriangle /> {t("admin_topicops_nickalti_warning")}
                </div>
              )}
              {moveSource && moveTarget &&
                moveSource.type === moveTarget.type && moveSource.id === moveTarget.id && (
                <div className="topicops-warning">
                  <FaExclamationTriangle /> {t("admin_topicops_same_topic_error")}
                </div>
              )}

              <button
                type="button"
                className="topicops-primary-btn"
                disabled={!moveReady || moving}
                onClick={handleMove}
              >
                {moving
                  ? t("loading", "Yükleniyor...")
                  : t("admin_topicops_move_button", { count: selectedIds.size })}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};